'use client';
import { OwnerService } from '../services/owner-service';
import { QUERY_KEYS } from './query-keys';
// Ajusta la ruta si es necesario
import { useAuth } from '@/backend/context/auth-management';
import type { ProfessionalWithId, Public } from '@/shared/schema';
import type { AdminSettings } from '@/shared/schema/admin';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeepPartial } from 'react-hook-form';
import { toast } from 'sonner';
import { adminSettingsRef } from '../services/helpers/firestore-refs';
import { getDoc } from 'firebase/firestore';
import { apiClient } from '../../api/api-client';

export const useOwner = {
  /**
   * Obtiene las configuraciones administrativas (Plan, Elite, etc.)
   * Estas son solo lectura para el Owner.
   */
  useAdminSettings: (uid: string | undefined) => {
    return useQuery({
      queryKey: ['admin-settings', uid],
      queryFn: async () => {
        const snap = await getDoc(adminSettingsRef(uid!));
        if (!snap.exists()) return null;
        return snap.data() as AdminSettings;
      },
      enabled: !!uid,
      staleTime: 1000 * 60 * 5, // 5 minutos de caché
    });
  },
  /**
   * 2. Obtener Perfil Estático
   * Útil cuando solo necesitas leer los datos en un componente que no es el AuthProvider
   */
  getById: (uid: string | undefined) => {
    return useQuery({
      queryKey: [QUERY_KEYS.OWNER_KEY, uid],
      queryFn: () => OwnerService.getById(uid!),
      enabled: !!uid,
    });
  },

  /**
   * 4. Actualizar Configuraciones (Branding, Tasa BCV, etc.)
   */
  updatePublic: () => {
    const queryClient = useQueryClient();
    const user = useAuth((s) => s.user); // 👈 Obtenemos el objeto user completo
    const ownerId = user?.uid;
    const slug = useAuth((s) => s.profile?.public?.branding?.slug);

    return useMutation({
      // mutationFn recibe el objeto que pasas en mutateAsync
      mutationFn: (publicData: DeepPartial<Public>) =>
        OwnerService.updatePublic(ownerId!, publicData),
      onSuccess: async (_, publicData) => {
        if (ownerId) {
          queryClient.setQueryData([QUERY_KEYS.OWNER_KEY, ownerId], (current: any) =>
            current
              ? {
                  ...current,
                  public: {
                    ...(current.public || {}),
                    ...(publicData || {}),
                  },
                }
              : current,
          );

          queryClient.setQueryData(['public', ownerId], (current: any) =>
            current ? { ...current, ...(publicData || {}) } : current,
          );

          if (slug) {
            queryClient.setQueryData([QUERY_KEYS.SLUG_KEY, 'profile', slug], (current: any) =>
              current ? { ...current, ...(publicData || {}) } : current,
            );
          }

          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.OWNER_KEY, ownerId],
          });
          // Importante: También invalidar la query pública si la usas en el admin
          queryClient.invalidateQueries({ queryKey: ['public', ownerId] });
        }

        // REVALIDATE NEXT.JS CACHE ON DEMAND
        if (slug && user) {
          try {
            apiClient
              .post('/api/revalidate', { slug, ownerId })
              .catch((e) =>
                console.error('[useOwner] Failed to ping revalidate API:', e),
              );
          } catch (e) {
            console.error(
              '[useOwner] Error triggering revalidation via apiClient:',
              e,
            );
          }
        }
      },
    });
  },
  update: () => {
    const queryClient = useQueryClient();
    const user = useAuth((s) => s.user);
    const ownerId = user?.uid;

    return useMutation({
      mutationFn: (data: Record<string, any>) => OwnerService.update(ownerId!, data as any),
      onSuccess: async (_, data) => {
        if (ownerId) {
          queryClient.setQueryData([QUERY_KEYS.OWNER_KEY, ownerId], (current: any) =>
            current ? { ...current, ...(data || {}) } : current,
          );

          queryClient.setQueryData(['public', ownerId], (current: any) =>
            current ? { ...current, ...(data || {}) } : current,
          );

          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.OWNER_KEY, ownerId],
          });
          queryClient.invalidateQueries({ queryKey: ['public', ownerId] });
        }
      },
    });
  },
};
